import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import AdminLayout, { AdminPageHead } from "./AdminLayout";
import { getJwtRole } from "../service/AuthService";
import { admin } from "../constants/Constants";
import { ADMIN_GUIDE } from "../constants/adminGuideContent";

// Admin-only, in-app reference covering every feature of the platform. Reached from the
// Admin page ("Guide" button). Gated the same way as the other /admin/* pages: a JWT-role
// check that redirects non-admins, not a route-level guard.

const GREEN = "var(--brand)";
const BLUE = "var(--series-blue)";

export default function AdminGuide() {
    const navigate = useNavigate();
    const [authed, setAuthed] = useState(false);
    const [search, setSearch] = useState("");
    const [activeCategory, setActiveCategory] = useState("all");
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        if (getJwtRole() !== admin) {
            navigate("/");
            return;
        }
        setAuthed(true);
    }, []);

    const categories = useMemo(() => {
        const seen = [];
        ADMIN_GUIDE.forEach((s) => { if (!seen.includes(s.category)) seen.push(s.category); });
        return seen;
    }, []);

    const q = search.trim().toLowerCase();

    const filtered = ADMIN_GUIDE.filter((s) => {
        if (activeCategory !== "all" && s.category !== activeCategory) return false;
        if (!q) return true;
        const hay = (
            s.title + " " + s.summary + " " +
            s.body.map((b) => b.heading + " " + (b.paragraphs || []).join(" ") + " " + (b.bullets || []).join(" ")).join(" ")
        ).toLowerCase();
        return hay.includes(q);
    });

    if (!authed) return null;

    const jumpTo = (id) => {
        setOpenId(id);
        setTimeout(() => document.getElementById("ag-" + id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
    };

    return (
        <AdminLayout>
                    <AdminPageHead
                        title="Admin Guide"
                        subtitle="A complete reference to every feature in DeepThought / 42Degrees."
                    />

                    {/* Search */}
                    <div style={{ position: "relative", maxWidth: 520, marginBottom: 14 }}>
                        <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                        <input
                            type="text"
                            placeholder="Search the guide…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px 10px 36px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 14, background: "var(--surface-2)", outline: "none", color: "var(--ink-900)" }}
                        />
                    </div>

                    {/* Category tabs */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                        {["all", ...categories].map((c) => {
                            const isActive = activeCategory === c;
                            return (
                                <button
                                    key={c}
                                    onClick={() => setActiveCategory(c)}
                                    style={{
                                        padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 500, cursor: "pointer",
                                        border: "1px solid " + (isActive ? GREEN : "var(--border)"),
                                        background: isActive ? GREEN : "var(--surface)",
                                        color: isActive ? "white" : "var(--ink-600)",
                                    }}
                                >
                                    {c === "all" ? "All" : c}
                                </button>
                            );
                        })}
                    </div>

                    {/* Quick-jump table of contents (hidden while searching) */}
                    {!q && (
                        <div className="submissions-card" style={{ padding: "14px 18px", marginBottom: 16 }}>
                            <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-300)" }}>On this page</p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                {filtered.map((s) => (
                                    <button
                                        key={s.id}
                                        onClick={() => jumpTo(s.id)}
                                        style={{ fontSize: 12.5, color: BLUE, background: "var(--info-bg)", border: "1px solid var(--info-border-soft)", borderRadius: 7, padding: "4px 10px", cursor: "pointer" }}
                                    >
                                        {s.title}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Sections */}
                    {filtered.length === 0 && (
                        <p style={{ color: "var(--ink-300)", fontSize: 14, padding: "30px 4px" }}>No matching topics.</p>
                    )}

                    {filtered.map((s) => {
                        const expanded = !!q || openId === s.id;
                        return (
                            <div className="submissions-card" id={"ag-" + s.id} key={s.id} style={{ marginBottom: 14, scrollMarginTop: 16 }}>
                                <button
                                    onClick={() => setOpenId(openId === s.id ? null : s.id)}
                                    style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 12, padding: "18px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: BLUE, background: "var(--info-bg)", borderRadius: 5, padding: "2px 7px", marginBottom: 7 }}>
                                            {s.category}
                                        </span>
                                        <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 600, color: "var(--ink-900)" }}>{s.title}</h3>
                                        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--ink-450)", lineHeight: 1.5 }}>{s.summary}</p>
                                    </div>
                                    <span style={{ color: "var(--ink-300)", marginTop: 4, flexShrink: 0 }}>
                                        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </span>
                                </button>

                                {expanded && (
                                    <div style={{ padding: "0 20px 20px", borderTop: "1px solid var(--border-subtle)" }}>
                                        {s.body.map((b, i) => (
                                            <div key={i} style={{ marginTop: 18 }}>
                                                <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 600, color: "var(--brand-text)" }}>{b.heading}</h3>
                                                {(b.paragraphs || []).map((p, j) => (
                                                    <p key={j} style={{ margin: "0 0 9px", fontSize: 13.5, color: "var(--ink-750)", lineHeight: 1.65 }}>{p}</p>
                                                ))}
                                                {b.bullets && b.bullets.length > 0 && (
                                                    <ul style={{ margin: "2px 0 9px", paddingLeft: 20 }}>
                                                        {b.bullets.map((bl, k) => (
                                                            <li key={k} style={{ fontSize: 13.5, color: "var(--ink-750)", lineHeight: 1.6, marginBottom: 4 }}>{bl}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

        </AdminLayout>
    );
}
